/**
 * `/admin/people/:personId` — admin person detail page (M13 slice).
 *
 * Loads `GET /people/:personId` and renders the person's core identity +
 * compliance-record summary + a "Mark NQA ITS synced" affordance + a
 * single-step archive panel (US-ADM-10) + an inline edit panel
 * (US-ADM-11, name / preferred_name / email / phone / role tags /
 * direct_child_contact / notes). Verification workflow (with WWCC help
 * panel), "upload on behalf", and per-record action affordances live on
 * the compliance-record detail page linked from each record row.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type {
  ArchivePersonResponse,
  AuStateCode,
  ComplianceRecord,
  ComplianceRecordStatus,
  NqaAddressType,
  NqaNatureOfEmployment,
  NqaNonEducatorRole,
  NqaPosition,
  Person,
  PersonDetailResponse,
  PersonHistoryEntry,
  PersonHistoryResponse,
  PersonStatus,
  PersonType,
  UpdatePersonRequest,
} from '@isms/shared';

export interface PersonDetailPageProps {
  client: ApiClient;
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'not_found' }
  | { kind: 'ready'; data: PersonDetailResponse };

const STATUS_LABELS: Record<PersonStatus, string> = {
  active: 'Active',
  onboarding: 'Onboarding',
  inactive: 'Inactive',
  stood_down: 'Stood down',
};

const PERSON_TYPE_LABELS: Record<PersonType, string> = {
  volunteer: 'Volunteer',
  practicum_student: 'Practicum student',
  observer: 'Observer',
  staff: 'Staff',
};

const RECORD_STATUS_LABELS: Record<ComplianceRecordStatus, string> = {
  pending: 'Pending',
  submitted: 'Awaiting verification',
  verified: 'Verified',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  superseded: 'Superseded',
  archived: 'Archived',
};

export function PersonDetailPage({
  client,
  signInPath = '/admin/sign-in',
}: PersonDetailPageProps): JSX.Element {
  const { personId } = useParams<{ personId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    if (!personId) {
      setState({ kind: 'not_found' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const data = await client.getAdminPerson(personId);
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load this person. Please check your connection and try again.",
      });
    }
  }, [client, personId, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyArchiveResult = useCallback((result: ArchivePersonResponse) => {
    setState((current) =>
      current.kind === 'ready'
        ? {
            kind: 'ready',
            data: {
              ...current.data,
              person: result.person,
            },
          }
        : current,
    );
  }, []);

  const applyPersonUpdate = useCallback((updated: Person) => {
    setState((current) =>
      current.kind === 'ready'
        ? {
            kind: 'ready',
            data: {
              ...current.data,
              person: updated,
            },
          }
        : current,
    );
  }, []);

  return (
    <main className="admin-person-detail" aria-labelledby="admin-person-detail-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/people">← Back to people</Link>
      </nav>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading person details…
        </p>
      ) : null}

      {state.kind === 'not_found' ? (
        <div className="admin-people-error" role="alert">
          <p>We couldn't find that person. They may have been archived or deleted.</p>
          <Link to="/admin/people">Return to the people list</Link>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <PersonDetailBody
          client={client}
          data={state.data}
          onArchiveChange={applyArchiveResult}
          onPersonUpdate={applyPersonUpdate}
          onUnauthorised={redirectToSignIn}
        />
      ) : null}
    </main>
  );
}

interface PersonDetailBodyProps {
  client: ApiClient;
  data: PersonDetailResponse;
  onArchiveChange: (result: ArchivePersonResponse) => void;
  onPersonUpdate: (updated: Person) => void;
  onUnauthorised: () => void;
}

function PersonDetailBody({
  client,
  data,
  onArchiveChange,
  onPersonUpdate,
  onUnauthorised,
}: PersonDetailBodyProps): JSX.Element {
  const { person, records, needs_nqa_its_sync } = data;
  return (
    <>
      <header className="admin-person-header">
        <h1 id="admin-person-detail-heading">{person.full_name}</h1>
        {person.preferred_name ? (
          <p className="admin-person-preferred">
            Also known as <strong>{person.preferred_name}</strong>
          </p>
        ) : null}
        <p className="admin-person-subtitle">
          <span>{PERSON_TYPE_LABELS[person.person_type]}</span>
          <span aria-hidden="true"> · </span>
          <span className={`admin-people-status admin-people-status-${person.status}`}>
            {STATUS_LABELS[person.status]}
          </span>
          {person.direct_child_contact ? (
            <>
              <span aria-hidden="true"> · </span>
              <span>Direct child contact</span>
            </>
          ) : null}
        </p>
      </header>

      <section className="admin-person-section">
        <h2>Contact</h2>
        <dl className="admin-person-dl">
          <dt>Email</dt>
          <dd>{person.contact_email}</dd>
          <dt>Phone</dt>
          <dd>{person.mobile_phone ?? '—'}</dd>
          <dt>Date of birth</dt>
          <dd>{person.date_of_birth ? toIsoDate(person.date_of_birth) : '—'}</dd>
          <dt>Start date</dt>
          <dd>{person.start_date ? toIsoDate(person.start_date) : '—'}</dd>
          <dt>End date</dt>
          <dd>{person.end_date ? toIsoDate(person.end_date) : '—'}</dd>
          {person.role_tags.length > 0 ? (
            <>
              <dt>Role tags</dt>
              <dd>{person.role_tags.join(', ')}</dd>
            </>
          ) : null}
        </dl>
      </section>

      <NqaItsFieldsBlock person={person} />

      <section className="admin-person-section">
        <h2>Compliance records</h2>
        {records.length === 0 ? (
          <p className="admin-people-empty">No compliance records yet.</p>
        ) : (
          <ul className="admin-person-records">
            {records.map((record) => (
              <ComplianceRecordRow key={toIdString(record.record_id)} record={record} />
            ))}
          </ul>
        )}
      </section>

      <NqaItsSyncPanel
        client={client}
        personId={toIdString(person.person_id)}
        person={person}
        needsSync={needs_nqa_its_sync}
        onUpdate={onPersonUpdate}
        onUnauthorised={onUnauthorised}
      />

      <HistoryTimelinePanel
        client={client}
        personId={toIdString(person.person_id)}
        onUnauthorised={onUnauthorised}
      />

      <EditPersonPanel
        client={client}
        person={person}
        onUpdate={onPersonUpdate}
        onUnauthorised={onUnauthorised}
      />

      <ArchivePanel
        client={client}
        personId={toIdString(person.person_id)}
        status={person.status}
        endDate={person.end_date}
        onChange={onArchiveChange}
        onUnauthorised={onUnauthorised}
      />

      <p className="admin-person-footnote">
        Supersede affordances on the per-record list land in a later admin slice.
      </p>
    </>
  );
}

interface ComplianceRecordRowProps {
  record: ComplianceRecord;
}

/**
 * NqaItsSyncPanel — admin "Mark NQA ITS synced" affordance. The dashboard
 * surfaces this volunteer when their `nqa_its_synced_at` is null or
 * older than `people.updated_at`. Clicking the button POSTs
 * `/people/:id/nqa-its-sync`, which stamps `nqa_its_synced_at = now()`
 * and (optionally) records the freshly-issued ACECQA `nqa_its_entry_id`.
 */
interface NqaItsSyncPanelProps {
  client: ApiClient;
  personId: string;
  person: Person;
  needsSync: boolean;
  onUpdate: (updated: Person) => void;
  onUnauthorised: () => void;
}

function NqaItsSyncPanel({
  client,
  personId,
  person,
  needsSync,
  onUpdate,
  onUnauthorised,
}: NqaItsSyncPanelProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [entryId, setEntryId] = useState(person.nqa_its_entry_id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | string | null>(null);

  const onSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setSubmitting(true);
      setError(null);
      try {
        const trimmed = entryId.trim();
        const result = await client.markPersonNqaItsSynced(personId, {
          nqa_its_entry_id: trimmed.length === 0 ? null : trimmed,
        });
        onUpdate(result.person);
        setEditing(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorised();
          return;
        }
        setError(
          err instanceof ApiError
            ? err
            : "We couldn't save the sync. Check your connection and try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [client, entryId, onUnauthorised, onUpdate, personId],
  );

  return (
    <section
      className="admin-person-section admin-person-nqa-its-sync"
      aria-labelledby="admin-person-nqa-its-sync-heading"
    >
      <h2 id="admin-person-nqa-its-sync-heading">NQA ITS sync</h2>
      <p>
        {needsSync
          ? person.nqa_its_synced_at
            ? `Last synced ${toIsoDate(person.nqa_its_synced_at)}. A regulator-relevant change has happened since — push the update to the ACECQA portal, then click below.`
            : 'This volunteer has never been synced to the ACECQA NQA ITS portal. Push their entry to the portal, then click below.'
          : person.nqa_its_synced_at
            ? `In sync — last marked ${toIsoDate(person.nqa_its_synced_at)}.`
            : 'In sync.'}
      </p>
      {editing ? (
        <form className="admin-person-nqa-its-sync-form" onSubmit={(ev) => void onSubmit(ev)}>
          <label
            htmlFor="admin-person-nqa-its-entry-id"
            className="admin-person-nqa-its-sync-label"
          >
            ACECQA NQA ITS entry id (optional)
          </label>
          <input
            id="admin-person-nqa-its-entry-id"
            type="text"
            value={entryId}
            onChange={(ev) => setEntryId(ev.target.value)}
            disabled={submitting}
            placeholder="e.g. 12345"
          />
          {error ? <ErrorBanner error={error} /> : null}
          <div className="admin-person-nqa-its-sync-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Mark NQA ITS synced'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={submitting}
              className="admin-person-nqa-its-sync-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="admin-person-nqa-its-sync-button"
          onClick={() => {
            setEntryId(person.nqa_its_entry_id ?? '');
            setEditing(true);
          }}
        >
          Mark NQA ITS synced
        </button>
      )}
    </section>
  );
}

function ComplianceRecordRow({ record }: ComplianceRecordRowProps): JSX.Element {
  const canVerify = record.status === 'pending' || record.status === 'submitted';
  return (
    <li className="admin-person-record">
      <span className="admin-person-record-status">{RECORD_STATUS_LABELS[record.status]}</span>
      <span className="admin-person-record-id">Record #{toIdString(record.record_id)}</span>
      {record.expiry_date ? (
        <span className="admin-person-record-expiry">Expires {toIsoDate(record.expiry_date)}</span>
      ) : null}
      {record.verified_on ? (
        <span className="admin-person-record-verified">
          Verified {toIsoDate(record.verified_on)}
        </span>
      ) : null}
      <Link
        className="admin-person-record-link"
        to={`/admin/compliance-records/${toIdString(record.record_id)}`}
      >
        {canVerify ? 'Open verification' : 'Open record'}
      </Link>
    </li>
  );
}

/**
 * ArchivePanel — single-step archive (US-ADM-10, PRD 06 §Person detail).
 * The "are you sure" step is the inline confirm form (reason textarea +
 * Archive button); there is no `pending_archive` intermediate state. A
 * single click on **Archive this volunteer** (after reason capture) flips
 * the person to `inactive`, archives every open compliance record,
 * revokes live sessions, and invalidates unused magic-link tokens. Once
 * inactive the panel renders a read-only summary; reactivation is not a
 * SPA action.
 */
interface ArchivePanelProps {
  client: ApiClient;
  personId: string;
  status: PersonStatus;
  endDate: Person['end_date'];
  onChange: (result: ArchivePersonResponse) => void;
  onUnauthorised: () => void;
}

type ArchivePanelMode = { kind: 'collapsed' } | { kind: 'editing' };

type ArchiveSubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; recordsArchived: number; sessionsRevoked: number }
  | { kind: 'error'; message: string; apiError: ApiError | null };

function ArchivePanel({
  client,
  personId,
  status,
  endDate,
  onChange,
  onUnauthorised,
}: ArchivePanelProps): JSX.Element {
  const [mode, setMode] = useState<ArchivePanelMode>({ kind: 'collapsed' });
  const [reason, setReason] = useState('');
  const [submit, setSubmit] = useState<ArchiveSubmitState>({ kind: 'idle' });

  const openForm = useCallback(() => {
    setReason('');
    setSubmit({ kind: 'idle' });
    setMode({ kind: 'editing' });
  }, []);

  const cancel = useCallback(() => {
    setMode({ kind: 'collapsed' });
    setSubmit({ kind: 'idle' });
  }, []);

  const onSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      const trimmedReason = reason.trim();
      if (trimmedReason.length === 0) {
        setSubmit({
          kind: 'error',
          message:
            'Please write a short reason so the audit trail records why this person is being archived.',
          apiError: null,
        });
        return;
      }
      setSubmit({ kind: 'submitting' });
      try {
        const result = await client.archiveAdminPerson(personId, { reason: trimmedReason });
        onChange(result);
        setSubmit({
          kind: 'success',
          recordsArchived: result.records_archived,
          sessionsRevoked: result.sessions_revoked,
        });
        setMode({ kind: 'collapsed' });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorised();
          return;
        }
        setSubmit({
          kind: 'error',
          message:
            err instanceof ApiError
              ? archiveErrorCopy(err)
              : "We couldn't save the change. Check your connection and try again — no changes were saved.",
          apiError: err instanceof ApiError ? err : null,
        });
      }
    },
    [client, onChange, onUnauthorised, personId, reason],
  );

  const isBusy = submit.kind === 'submitting';

  if (status === 'inactive') {
    const justArchived = submit.kind === 'success' ? submit : null;
    return (
      <section
        className="admin-person-section admin-person-archive admin-person-archive-inactive"
        aria-labelledby="admin-person-archive-heading"
      >
        <h2 id="admin-person-archive-heading">Archive</h2>
        <p role="status" data-testid="admin-person-archive-inactive-summary">
          {endDate
            ? `This volunteer is archived with an end date of ${toIsoDate(endDate)}. Their compliance records are retained in read-only form for audit and retention purposes.`
            : 'This volunteer is archived. Their compliance records are retained in read-only form for audit and retention purposes.'}
        </p>
        {justArchived ? (
          <p className="admin-person-archive-success" role="status">
            {`Archive complete. ${justArchived.recordsArchived} compliance ${
              justArchived.recordsArchived === 1 ? 'record was' : 'records were'
            } archived and ${justArchived.sessionsRevoked} sign-in ${
              justArchived.sessionsRevoked === 1 ? 'session was' : 'sessions were'
            } revoked.`}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className="admin-person-section admin-person-archive"
      aria-labelledby="admin-person-archive-heading"
    >
      <h2 id="admin-person-archive-heading">Archive</h2>

      <p className="admin-person-archive-hint">
        Stop chasing this volunteer for compliance reminders. Archiving flips the person to
        inactive, archives every open compliance record, revokes any live sign-in sessions, and
        invalidates unused sign-in links. Records are kept — they are never hard-deleted.
      </p>

      {mode.kind === 'collapsed' ? (
        <div className="admin-person-archive-actions">
          <button type="button" className="admin-person-archive-button" onClick={openForm}>
            Archive this volunteer
          </button>
        </div>
      ) : (
        <form className="admin-person-archive-form" onSubmit={(ev) => void onSubmit(ev)} noValidate>
          <label htmlFor="admin-person-archive-reason" className="admin-person-archive-label">
            Why is this volunteer being archived?
            <span className="admin-person-archive-required" aria-hidden="true">
              {' '}
              *
            </span>
            <span className="sr-only"> (required)</span>
          </label>
          <textarea
            id="admin-person-archive-reason"
            className="admin-person-archive-textarea"
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            rows={3}
            placeholder="Left the organisation — confirmed via email on 2026-04-20."
            disabled={isBusy}
            required
          />

          {submit.kind === 'error' ? (
            <ErrorBanner error={submit.apiError ?? submit.message} message={submit.message} />
          ) : null}

          <div className="admin-person-archive-actions">
            <button type="submit" className="admin-person-archive-confirm-button" disabled={isBusy}>
              {isBusy ? 'Archiving…' : 'Archive — move to inactive'}
            </button>
            <button
              type="button"
              className="admin-person-archive-secondary-button"
              onClick={cancel}
              disabled={isBusy}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function archiveErrorCopy(err: ApiError): string {
  switch (err.code) {
    case 'person_not_found':
      return 'This person no longer exists. Reload the page to see the current state.';
    case 'already_inactive':
      return 'This person is already archived. Reload the page to see the current state.';
    case 'validation_failed':
      return 'Please check the reason, then try again.';
    default:
      return err.message;
  }
}

/**
 * EditPersonPanel — US-ADM-11 inline edit affordance on the person-detail
 * page. Sends only the fields that actually changed (a minimal PATCH) so
 * the `audit_events.after` payload reads as the minimal diff. Renders a
 * plain-English warning when the email is about to change so the admin
 * knows the cascade will happen (every active volunteer session revoked,
 * every unused magic-link token invalidated — all inside the same DB
 * transaction on the server). We do not support editing start_date / DOB
 * / person_type / status here — those are either rarely changed or sit
 * behind their own dedicated affordances (status is owned by archive /
 * stand-down, person_type is fixed at create).
 *
 * The read-only branch is rendered when the person is no longer active —
 * editing an inactive / stood_down person inline would be surprising.
 * The admin can restore the person via their normal workflow if
 * correction is needed.
 */
interface EditPersonPanelProps {
  client: ApiClient;
  person: Person;
  onUpdate: (updated: Person) => void;
  onUnauthorised: () => void;
}

interface EditPersonFormState {
  preferred_name: string;
  contact_email: string;
  role_tags: string;
  direct_child_contact: boolean;
  notes: string;
  // NQA ITS Worker Register fields (migration 0019). Selects/enums use
  // '' to mean "null / not set" so the diff in buildPatch can reliably
  // map blanks back to null without losing the "unset" state.
  title: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  names_previously_known_as: string;
  alias_other_names_known_by: string;
  // Single phone field (migration 0034). Labelled "Phone" in the UI.
  mobile_phone: string;
  nqa_position: '' | NqaPosition;
  non_educator_role: '' | NqaNonEducatorRole;
  nature_of_employment: NqaNatureOfEmployment;
  on_probationary_period: boolean;
  wwcc_exemption: boolean;
  wwcc_exemption_reason: string;
  no_qualification_training: boolean;
  address_type: '' | NqaAddressType;
  address_line_1: string;
  address_line_2: string;
  address_suburb: string;
  address_state: '' | AuStateCode;
  address_postcode: string;
}

const ADDRESS_TYPE_OPTIONS: readonly NqaAddressType[] = ['Residential', 'Postal', 'Other'];

const AU_STATE_OPTIONS: readonly AuStateCode[] = [
  'ACT',
  'NSW',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
  'NT',
];

type EditSubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; emailChanged: boolean }
  | { kind: 'error'; message: string; apiError: ApiError | null };

function personToFormState(person: Person): EditPersonFormState {
  return {
    preferred_name: person.preferred_name ?? '',
    contact_email: person.contact_email,
    role_tags: person.role_tags.join(', '),
    direct_child_contact: person.direct_child_contact,
    notes: person.notes ?? '',
    title: person.title ?? '',
    first_name: person.first_name,
    middle_name: person.middle_name ?? '',
    last_name: person.last_name,
    names_previously_known_as: person.names_previously_known_as ?? '',
    alias_other_names_known_by: person.alias_other_names_known_by ?? '',
    mobile_phone: person.mobile_phone ?? '',
    nqa_position: person.nqa_position ?? '',
    non_educator_role: person.non_educator_role ?? '',
    nature_of_employment: person.nature_of_employment,
    on_probationary_period: person.on_probationary_period === true,
    wwcc_exemption: person.wwcc_exemption,
    wwcc_exemption_reason: person.wwcc_exemption_reason ?? '',
    no_qualification_training: person.no_qualification_training,
    address_type: person.address_type ?? '',
    address_line_1: person.address_line_1 ?? '',
    address_line_2: person.address_line_2 ?? '',
    address_suburb: person.address_suburb ?? '',
    address_state: person.address_state ?? '',
    address_postcode: person.address_postcode ?? '',
  };
}

function buildPatch(person: Person, form: EditPersonFormState): UpdatePersonRequest {
  const patch: Record<string, unknown> = {};

  const trimmedPreferred = form.preferred_name.trim();
  const preferredNext: string | null = trimmedPreferred === '' ? null : trimmedPreferred;
  if (preferredNext !== (person.preferred_name ?? null)) {
    patch.preferred_name = preferredNext;
  }

  const trimmedEmail = form.contact_email.trim();
  if (trimmedEmail.toLowerCase() !== person.contact_email.toLowerCase()) {
    patch.contact_email = trimmedEmail;
  }

  // mobile_phone is intentionally not editable in the slim v1 admin form;
  // the volunteer maintains it themselves on /me.

  const parsedRoleTags = parseRoleTags(form.role_tags);
  if (!stringArraysEqual(parsedRoleTags, person.role_tags)) {
    patch.role_tags = parsedRoleTags;
  }

  if (form.direct_child_contact !== person.direct_child_contact) {
    patch.direct_child_contact = form.direct_child_contact;
  }

  const trimmedNotes = form.notes;
  const notesNext: string | null = trimmedNotes === '' ? null : trimmedNotes;
  if (notesNext !== (person.notes ?? null)) {
    patch.notes = notesNext;
  }

  // ------------------------------------------------------------------
  // NQA ITS Worker Register fields (migration 0019). Each field follows
  // the same pattern: trim/coerce, compare against the current row
  // value, emit into the patch only when the two differ. Enums that the
  // form represents as `''` for "unset" map to `null` on the wire.
  // ------------------------------------------------------------------

  const titleNext: string | null = nullIfBlank(form.title);
  if (titleNext !== (person.title ?? null)) patch.title = titleNext;

  const firstNameNext = form.first_name.trim();
  if (firstNameNext.length > 0 && firstNameNext !== person.first_name) {
    patch.first_name = firstNameNext;
  }

  const middleNext: string | null = nullIfBlank(form.middle_name);
  if (middleNext !== (person.middle_name ?? null)) patch.middle_name = middleNext;

  const lastNameNext = form.last_name.trim();
  if (lastNameNext.length > 0 && lastNameNext !== person.last_name) {
    patch.last_name = lastNameNext;
  }

  // full_name is no longer admin-editable, and migration 0034 dropped the
  // stored column entirely — the server projects it from first / middle /
  // last via concat_ws on every read. Nothing to patch here.

  const previouslyKnownNext: string | null = nullIfBlank(form.names_previously_known_as);
  if (previouslyKnownNext !== (person.names_previously_known_as ?? null)) {
    patch.names_previously_known_as = previouslyKnownNext;
  }

  const aliasNext: string | null = nullIfBlank(form.alias_other_names_known_by);
  if (aliasNext !== (person.alias_other_names_known_by ?? null)) {
    patch.alias_other_names_known_by = aliasNext;
  }

  const mobilePhoneNext: string | null = nullIfBlank(form.mobile_phone);
  if (mobilePhoneNext !== (person.mobile_phone ?? null)) patch.mobile_phone = mobilePhoneNext;

  const nqaPositionNext: NqaPosition | null = form.nqa_position === '' ? null : form.nqa_position;
  if (nqaPositionNext !== (person.nqa_position ?? null)) patch.nqa_position = nqaPositionNext;

  // `non_educator_role` is only meaningful when `nqa_position === 'Non-Educator Staff'`.
  // When switching away, send `null` so the server drops the role; when switching to, the
  // admin picks a new role. Diff against the stored row, not the new nqa_position.
  const nonEducatorRoleFromForm: NqaNonEducatorRole | null =
    form.nqa_position === 'Non-Educator Staff' && form.non_educator_role !== ''
      ? form.non_educator_role
      : null;
  if (nonEducatorRoleFromForm !== (person.non_educator_role ?? null)) {
    patch.non_educator_role = nonEducatorRoleFromForm;
  }

  if (form.nature_of_employment !== person.nature_of_employment) {
    patch.nature_of_employment = form.nature_of_employment;
  }

  const probationNext: boolean | null = form.on_probationary_period ? true : null;
  if (probationNext !== (person.on_probationary_period ?? null)) {
    patch.on_probationary_period = probationNext;
  }

  if (form.wwcc_exemption !== person.wwcc_exemption) {
    patch.wwcc_exemption = form.wwcc_exemption;
  }

  // Reason only meaningful when wwcc_exemption is true. Mirror the
  // server CHECK by clearing the reason when the checkbox unticks.
  const exemptionReasonFromForm: string | null = form.wwcc_exemption
    ? nullIfBlank(form.wwcc_exemption_reason)
    : null;
  if (exemptionReasonFromForm !== (person.wwcc_exemption_reason ?? null)) {
    patch.wwcc_exemption_reason = exemptionReasonFromForm;
  }

  if (form.no_qualification_training !== person.no_qualification_training) {
    patch.no_qualification_training = form.no_qualification_training;
  }

  const addressTypeNext: NqaAddressType | null =
    form.address_type === '' ? null : form.address_type;
  if (addressTypeNext !== (person.address_type ?? null)) patch.address_type = addressTypeNext;

  const addressLine1Next: string | null = nullIfBlank(form.address_line_1);
  if (addressLine1Next !== (person.address_line_1 ?? null)) patch.address_line_1 = addressLine1Next;

  const addressLine2Next: string | null = nullIfBlank(form.address_line_2);
  if (addressLine2Next !== (person.address_line_2 ?? null)) patch.address_line_2 = addressLine2Next;

  const addressSuburbNext: string | null = nullIfBlank(form.address_suburb);
  if (addressSuburbNext !== (person.address_suburb ?? null))
    patch.address_suburb = addressSuburbNext;

  const addressStateNext: AuStateCode | null =
    form.address_state === '' ? null : form.address_state;
  if (addressStateNext !== (person.address_state ?? null)) patch.address_state = addressStateNext;

  const addressPostcodeNext: string | null = nullIfBlank(form.address_postcode);
  if (addressPostcodeNext !== (person.address_postcode ?? null))
    patch.address_postcode = addressPostcodeNext;

  return patch as UpdatePersonRequest;
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function parseRoleTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

function stringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function EditPersonPanel({
  client,
  person,
  onUpdate,
  onUnauthorised,
}: EditPersonPanelProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<EditPersonFormState>(() => personToFormState(person));
  const [submit, setSubmit] = useState<EditSubmitState>({ kind: 'idle' });

  const isReadOnly = person.status === 'inactive' || person.status === 'stood_down';

  const resetForm = useCallback(() => {
    setForm(personToFormState(person));
    setSubmit({ kind: 'idle' });
  }, [person]);

  const openEdit = useCallback(() => {
    resetForm();
    setExpanded(true);
  }, [resetForm]);

  const cancel = useCallback(() => {
    setExpanded(false);
    resetForm();
  }, [resetForm]);

  const onSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();

      if (form.first_name.trim().length === 0 || form.last_name.trim().length === 0) {
        setSubmit({
          kind: 'error',
          message: 'First name and last name are both required.',
          apiError: null,
        });
        return;
      }
      if (form.contact_email.trim().length === 0) {
        setSubmit({
          kind: 'error',
          message: 'Email can’t be empty — the volunteer uses it to sign in.',
          apiError: null,
        });
        return;
      }

      const patch = buildPatch(person, form);
      if (Object.keys(patch).length === 0) {
        setSubmit({
          kind: 'error',
          message: 'Change at least one field before saving.',
          apiError: null,
        });
        return;
      }

      setSubmit({ kind: 'submitting' });
      try {
        const updated = await client.updateAdminPerson(String(person.person_id), patch);
        onUpdate(updated);
        const emailChanged = patch.contact_email !== undefined;
        setSubmit({ kind: 'success', emailChanged });
        setExpanded(false);
        setForm(personToFormState(updated));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorised();
          return;
        }
        setSubmit({
          kind: 'error',
          message:
            err instanceof ApiError
              ? editErrorCopy(err)
              : "We couldn't save the change. Check your connection and try again — no changes were saved.",
          apiError: err instanceof ApiError ? err : null,
        });
      }
    },
    [client, form, onUnauthorised, onUpdate, person],
  );

  const isBusy = submit.kind === 'submitting';
  const emailWillChange =
    form.contact_email.trim().toLowerCase() !== person.contact_email.toLowerCase();

  if (isReadOnly) {
    return (
      <section
        className="admin-person-section admin-person-edit"
        aria-labelledby="admin-person-edit-heading"
      >
        <h2 id="admin-person-edit-heading">Edit details</h2>
        <p className="admin-person-edit-readonly">
          This volunteer is {STATUS_LABELS[person.status].toLowerCase()} — details are read-only.
          Use the archive panel to change their status.
        </p>
      </section>
    );
  }

  return (
    <section
      className="admin-person-section admin-person-edit"
      aria-labelledby="admin-person-edit-heading"
    >
      <h2 id="admin-person-edit-heading">Edit details</h2>

      {!expanded ? (
        <>
          <p className="admin-person-edit-hint">
            Update the volunteer’s name, email, role tags, or notes. Changing the email signs the
            volunteer out of every active session and invalidates any unused sign-in links — they
            will need to sign in again with the new email to reach their dashboard.
          </p>
          <div className="admin-person-edit-actions">
            <button type="button" className="admin-person-edit-button" onClick={openEdit}>
              Edit details
            </button>
          </div>
          {submit.kind === 'success' ? (
            <p className="admin-person-edit-success" role="status">
              {submit.emailChanged
                ? 'Saved. Any active sessions and unused sign-in links have been revoked — the volunteer will need to sign in with the new email.'
                : 'Saved.'}
            </p>
          ) : null}
        </>
      ) : null}

      {expanded ? (
        <form className="admin-person-edit-form" onSubmit={onSubmit} noValidate>
          <div className="admin-person-edit-field">
            <label htmlFor="admin-person-edit-preferred-name" className="admin-person-edit-label">
              Preferred name
            </label>
            <input
              id="admin-person-edit-preferred-name"
              className="admin-person-edit-input"
              type="text"
              value={form.preferred_name}
              onChange={(ev) => setForm((f) => ({ ...f, preferred_name: ev.target.value }))}
              disabled={isBusy}
              placeholder="Leave blank if none."
            />
          </div>

          <div className="admin-person-edit-field">
            <label htmlFor="admin-person-edit-email" className="admin-person-edit-label">
              Email
              <span className="admin-person-edit-required" aria-hidden="true">
                {' '}
                *
              </span>
              <span className="sr-only"> (required)</span>
            </label>
            <input
              id="admin-person-edit-email"
              className="admin-person-edit-input"
              type="email"
              value={form.contact_email}
              onChange={(ev) => setForm((f) => ({ ...f, contact_email: ev.target.value }))}
              disabled={isBusy}
              autoComplete="off"
              required
            />
            {emailWillChange ? (
              <p className="admin-person-edit-warning" role="status">
                Changing this email signs the volunteer out of every active session and invalidates
                any unused sign-in links when you save.
              </p>
            ) : null}
          </div>

          <div className="admin-person-edit-field">
            <label htmlFor="admin-person-edit-role-tags" className="admin-person-edit-label">
              Role tags
            </label>
            <input
              id="admin-person-edit-role-tags"
              className="admin-person-edit-input"
              type="text"
              value={form.role_tags}
              onChange={(ev) => setForm((f) => ({ ...f, role_tags: ev.target.value }))}
              disabled={isBusy}
              placeholder="tutor, mentor"
            />
            <p className="admin-person-edit-hint-inline">Comma-separated. Leave blank for none.</p>
          </div>

          <div className="admin-person-edit-field admin-person-edit-checkbox-field">
            <label
              htmlFor="admin-person-edit-direct-child-contact"
              className="admin-person-edit-label"
            >
              <input
                id="admin-person-edit-direct-child-contact"
                type="checkbox"
                checked={form.direct_child_contact}
                onChange={(ev) =>
                  setForm((f) => ({ ...f, direct_child_contact: ev.target.checked }))
                }
                disabled={isBusy}
              />
              <span>Direct child contact</span>
            </label>
          </div>

          <div className="admin-person-edit-field">
            <label htmlFor="admin-person-edit-notes" className="admin-person-edit-label">
              Admin notes
            </label>
            <textarea
              id="admin-person-edit-notes"
              className="admin-person-edit-textarea"
              value={form.notes}
              onChange={(ev) => setForm((f) => ({ ...f, notes: ev.target.value }))}
              rows={3}
              disabled={isBusy}
              placeholder="Internal notes for the compliance team. Never shown to the volunteer."
            />
          </div>

          <NqaItsFieldsEditor form={form} setForm={setForm} isBusy={isBusy} />

          {submit.kind === 'error' ? (
            <ErrorBanner error={submit.apiError ?? submit.message} message={submit.message} />
          ) : null}

          <div className="admin-person-edit-actions">
            <button type="submit" className="admin-person-edit-button" disabled={isBusy}>
              {isBusy ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              className="admin-person-edit-secondary-button"
              onClick={cancel}
              disabled={isBusy}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function editErrorCopy(err: ApiError): string {
  switch (err.code) {
    case 'person_not_found':
      return 'This person no longer exists. Reload the page to see the current state.';
    case 'email_in_use':
      return 'That email is already on file for another person. Please use a different address.';
    case 'non_educator_role_only_for_non_educator_staff':
      return 'A non-educator role can only be set when the NQA position is Non-Educator Staff. Clear the role or change the position.';
    case 'wwcc_exemption_reason_requires_exemption':
      return 'A WWCC exemption reason can only be supplied when the WWCC exemption checkbox is ticked.';
    case 'validation_failed':
      return 'Please check the fields you changed, then try again.';
    default:
      return err.message;
  }
}

function toIdString(id: ComplianceRecord['record_id']): string {
  return typeof id === 'string' ? id : String(id);
}

function toIsoDate(v: string | Date): string {
  const s = typeof v === 'string' ? v : v.toISOString();
  return s.slice(0, 10);
}

// ---------------------------------------------------------------------------
// NQA ITS Worker Register fields (migration 0019) — read-only block +
// edit-panel extension used by the admin SPA (Layer 3). The read-only
// block renders on every mount so admins can see the values without
// opening the editor; the edit-panel extension is mounted inside the
// existing EditPersonPanel form.
// ---------------------------------------------------------------------------

interface NqaItsFieldsBlockProps {
  person: Person;
}

function NqaItsFieldsBlock({ person }: NqaItsFieldsBlockProps): JSX.Element {
  return (
    <section className="admin-person-section" data-testid="admin-person-nqa-its-block">
      <h2>NQA ITS fields</h2>
      <dl className="admin-person-dl">
        <dt>Title</dt>
        <dd>{renderNullable(person.title)}</dd>
        <dt>First name</dt>
        <dd>{person.first_name}</dd>
        <dt>Middle name</dt>
        <dd>{renderNullable(person.middle_name)}</dd>
        <dt>Last name</dt>
        <dd>{person.last_name}</dd>
        <dt>Names previously known as</dt>
        <dd>{renderNullable(person.names_previously_known_as)}</dd>
        <dt>Alias / other names known by</dt>
        <dd>{renderNullable(person.alias_other_names_known_by)}</dd>
        <dt>NQA position</dt>
        <dd>{renderNullable(person.nqa_position)}</dd>
        <dt>Non-educator role</dt>
        <dd>{renderNullable(person.non_educator_role)}</dd>
        <dt>Nature of employment</dt>
        <dd>{person.nature_of_employment}</dd>
        <dt>On probationary period</dt>
        <dd>
          {person.on_probationary_period === null
            ? '—'
            : person.on_probationary_period
              ? 'Yes'
              : 'No'}
        </dd>
        <dt>WWCC exemption</dt>
        <dd>{person.wwcc_exemption ? 'Yes' : 'No'}</dd>
        <dt>WWCC exemption reason</dt>
        <dd>{renderNullable(person.wwcc_exemption_reason)}</dd>
        <dt>No qualifications / training</dt>
        <dd>{person.no_qualification_training ? 'Yes' : 'No'}</dd>
        <dt>Address type</dt>
        <dd>{renderNullable(person.address_type)}</dd>
        <dt>Address line 1</dt>
        <dd>{renderNullable(person.address_line_1)}</dd>
        <dt>Address line 2</dt>
        <dd>{renderNullable(person.address_line_2)}</dd>
        <dt>Suburb</dt>
        <dd>{renderNullable(person.address_suburb)}</dd>
        <dt>State</dt>
        <dd>{renderNullable(person.address_state)}</dd>
        <dt>Postcode</dt>
        <dd>{renderNullable(person.address_postcode)}</dd>
      </dl>
    </section>
  );
}

function renderNullable(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

interface NqaItsFieldsEditorProps {
  form: EditPersonFormState;
  setForm: React.Dispatch<React.SetStateAction<EditPersonFormState>>;
  isBusy: boolean;
}

function NqaItsFieldsEditor({ form, setForm, isBusy }: NqaItsFieldsEditorProps): JSX.Element {
  return (
    <>
      <fieldset className="admin-person-form-fieldset">
        <legend>Name (NQA ITS)</legend>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-title" className="admin-person-edit-label">
            Title
          </label>
          <input
            id="admin-person-edit-title"
            className="admin-person-edit-input"
            type="text"
            value={form.title}
            onChange={(ev) => setForm((f) => ({ ...f, title: ev.target.value }))}
            disabled={isBusy}
            placeholder="Mr / Ms / Dr — optional"
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-first-name" className="admin-person-edit-label">
            First name
          </label>
          <input
            id="admin-person-edit-first-name"
            className="admin-person-edit-input"
            type="text"
            value={form.first_name}
            onChange={(ev) => setForm((f) => ({ ...f, first_name: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-middle-name" className="admin-person-edit-label">
            Middle name
          </label>
          <input
            id="admin-person-edit-middle-name"
            className="admin-person-edit-input"
            type="text"
            value={form.middle_name}
            onChange={(ev) => setForm((f) => ({ ...f, middle_name: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-last-name" className="admin-person-edit-label">
            Last name
          </label>
          <input
            id="admin-person-edit-last-name"
            className="admin-person-edit-input"
            type="text"
            value={form.last_name}
            onChange={(ev) => setForm((f) => ({ ...f, last_name: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-names-previously" className="admin-person-edit-label">
            Names previously known as
          </label>
          <input
            id="admin-person-edit-names-previously"
            className="admin-person-edit-input"
            type="text"
            value={form.names_previously_known_as}
            onChange={(ev) =>
              setForm((f) => ({ ...f, names_previously_known_as: ev.target.value }))
            }
            disabled={isBusy}
            placeholder="e.g. maiden name"
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-alias" className="admin-person-edit-label">
            Alias / other names known by
          </label>
          <input
            id="admin-person-edit-alias"
            className="admin-person-edit-input"
            type="text"
            value={form.alias_other_names_known_by}
            onChange={(ev) =>
              setForm((f) => ({ ...f, alias_other_names_known_by: ev.target.value }))
            }
            disabled={isBusy}
          />
        </div>
      </fieldset>

      <fieldset className="admin-person-form-fieldset">
        <legend>Address</legend>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-type" className="admin-person-edit-label">
            Address type
          </label>
          <select
            id="admin-person-edit-address-type"
            className="admin-person-edit-input"
            value={form.address_type}
            onChange={(ev) =>
              setForm((f) => ({
                ...f,
                address_type: ev.target.value as EditPersonFormState['address_type'],
              }))
            }
            disabled={isBusy}
          >
            <option value="">— Select —</option>
            {ADDRESS_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-line-1" className="admin-person-edit-label">
            Address line 1
          </label>
          <input
            id="admin-person-edit-address-line-1"
            className="admin-person-edit-input"
            type="text"
            value={form.address_line_1}
            onChange={(ev) => setForm((f) => ({ ...f, address_line_1: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-line-2" className="admin-person-edit-label">
            Address line 2
          </label>
          <input
            id="admin-person-edit-address-line-2"
            className="admin-person-edit-input"
            type="text"
            value={form.address_line_2}
            onChange={(ev) => setForm((f) => ({ ...f, address_line_2: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-suburb" className="admin-person-edit-label">
            Suburb
          </label>
          <input
            id="admin-person-edit-address-suburb"
            className="admin-person-edit-input"
            type="text"
            value={form.address_suburb}
            onChange={(ev) => setForm((f) => ({ ...f, address_suburb: ev.target.value }))}
            disabled={isBusy}
          />
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-state" className="admin-person-edit-label">
            State
          </label>
          <select
            id="admin-person-edit-address-state"
            className="admin-person-edit-input"
            value={form.address_state}
            onChange={(ev) =>
              setForm((f) => ({
                ...f,
                address_state: ev.target.value as EditPersonFormState['address_state'],
              }))
            }
            disabled={isBusy}
          >
            <option value="">— Select —</option>
            {AU_STATE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-person-edit-field">
          <label htmlFor="admin-person-edit-address-postcode" className="admin-person-edit-label">
            Postcode
          </label>
          <input
            id="admin-person-edit-address-postcode"
            className="admin-person-edit-input"
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            value={form.address_postcode}
            onChange={(ev) => setForm((f) => ({ ...f, address_postcode: ev.target.value }))}
            disabled={isBusy}
            placeholder="4-digit postcode"
          />
        </div>
      </fieldset>
    </>
  );
}

// ---------------------------------------------------------------------------
// History timeline (PRD 06 §People list — detail view, "every reminder
// sent and every audit event touching this person")
// ---------------------------------------------------------------------------

interface HistoryTimelinePanelProps {
  client: ApiClient;
  personId: string;
  onUnauthorised: () => void;
  /** Override for tests so they can hit smaller pages without seeding 100 rows. */
  limit?: number;
}

type HistoryLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: PersonHistoryResponse };

function HistoryTimelinePanel({
  client,
  personId,
  onUnauthorised,
  limit,
}: HistoryTimelinePanelProps): JSX.Element {
  const [state, setState] = useState<HistoryLoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await client.getAdminPersonHistory(personId, limit ? { limit } : undefined);
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorised();
        return;
      }
      const error =
        err instanceof ApiError
          ? err
          : 'We could not load the history. Please try again in a moment.';
      setState({ kind: 'error', error });
    }
  }, [client, personId, limit, onUnauthorised]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="admin-person-section" data-testid="admin-person-history">
      <h2>History</h2>
      <p className="admin-person-history-hint">
        Every reminder we sent this volunteer and every action we recorded against them or their
        compliance records, newest first.
      </p>
      {state.kind === 'loading' ? (
        <p className="admin-people-empty">Loading history…</p>
      ) : state.kind === 'error' ? (
        <div className="admin-person-history-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()} className="admin-person-button">
            Try again
          </button>
        </div>
      ) : state.data.items.length === 0 ? (
        <p className="admin-people-empty">No history to show yet.</p>
      ) : (
        <>
          <ol className="admin-person-history-list">
            {state.data.items.map((entry) => (
              <HistoryEntryRow key={historyEntryKey(entry)} entry={entry} />
            ))}
          </ol>
          {state.data.truncated ? (
            <p className="admin-person-history-truncated">
              Showing the {state.data.items.length} most recent of {state.data.total_candidates}{' '}
              entries. Older history is available in the audit-log export.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function historyEntryKey(entry: PersonHistoryEntry): string {
  return entry.kind === 'reminder'
    ? `reminder-${toIdString(entry.reminder_id)}`
    : `audit-${toIdString(entry.audit_event_id)}`;
}

interface HistoryEntryRowProps {
  entry: PersonHistoryEntry;
}

function HistoryEntryRow({ entry }: HistoryEntryRowProps): JSX.Element {
  const occurredIso = toIsoDateTime(entry.occurred_at);
  const occurredLabel = formatTimelineDate(occurredIso);
  if (entry.kind === 'reminder') {
    return (
      <li className="admin-person-history-row admin-person-history-row-reminder">
        <span className="admin-person-history-icon" aria-hidden="true">
          ✉
        </span>
        <div className="admin-person-history-body">
          <p className="admin-person-history-title">
            {reminderTitle(entry.offset_days, entry.requirement_name)}
          </p>
          <p className="admin-person-history-meta">
            <time dateTime={occurredIso}>{occurredLabel}</time>
            <span aria-hidden="true"> · </span>
            <span>Template: {entry.template}</span>
            <span aria-hidden="true"> · </span>
            <span>{deliveryStatusCopy(entry.delivery_status)}</span>
          </p>
        </div>
      </li>
    );
  }
  return (
    <li className="admin-person-history-row admin-person-history-row-audit">
      <span className="admin-person-history-icon" aria-hidden="true">
        ◉
      </span>
      <div className="admin-person-history-body">
        <p className="admin-person-history-title">
          {auditActionLabel(entry.action)}
          {entry.entity_link ? (
            <>
              {' '}
              <Link to={entityLinkHref(entry.entity_link)} className="admin-person-history-link">
                {entry.entity_summary}
              </Link>
            </>
          ) : (
            <span className="admin-person-history-entity"> {entry.entity_summary}</span>
          )}
        </p>
        <p className="admin-person-history-meta">
          <time dateTime={occurredIso}>{occurredLabel}</time>
          <span aria-hidden="true"> · </span>
          <span>By {actorLabel(entry.actor_kind, entry.actor_display)}</span>
        </p>
      </div>
    </li>
  );
}

function toIsoDateTime(v: string | Date): string {
  return typeof v === 'string' ? v : v.toISOString();
}

function reminderTitle(offsetDays: number, itemTypeName: string): string {
  if (offsetDays > 0) {
    return `Reminder sent · ${itemTypeName} expiring in ${offsetDays} days`;
  }
  if (offsetDays === 0) {
    return `Reminder sent · ${itemTypeName} expiring today`;
  }
  return `Escalation sent · ${itemTypeName} ${Math.abs(offsetDays)} days overdue`;
}

function deliveryStatusCopy(status: 'sent' | 'bounced' | 'failed'): string {
  switch (status) {
    case 'sent':
      return 'Delivered';
    case 'bounced':
      return 'Bounced — admin follow-up may be needed';
    case 'failed':
      return 'Failed to send';
  }
}

function actorLabel(kind: 'admin' | 'volunteer' | 'system', display: string | null): string {
  if (kind === 'system') return 'the system';
  return display ?? (kind === 'admin' ? 'an admin' : 'this volunteer');
}

function entityLinkHref(
  link: NonNullable<PersonHistoryEntry & { kind: 'audit_event' }>['entity_link'],
): string {
  // The discriminated union narrows the audit branch — link is always
  // present here.
  if (!link) return '#';
  switch (link.kind) {
    case 'compliance_record':
      return `/admin/compliance-records/${encodeURIComponent(toIdString(link.id))}`;
    case 'person':
      return `/admin/people/${encodeURIComponent(toIdString(link.id))}`;
    case 'evidence':
    case 'acknowledgement_signature':
      // No dedicated SPA detail screens for these yet — fall back to
      // the audit summary text rendered alongside.
      return '#';
  }
}

const TIMELINE_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatTimelineDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return TIMELINE_FORMATTER.format(d);
}

/**
 * Friendly label for an `audit_events.action` string. Falls back to
 * humanising the dotted segment when the action isn't known so a new
 * action type added server-side still renders something readable
 * without a UI change.
 */
function auditActionLabel(action: string): string {
  const map: Record<string, string> = {
    'people.create': 'Volunteer created',
    'people.update': 'Volunteer details updated',
    'people.archive': 'Volunteer archived',
    'people.intake.submitted': 'Intake form submitted',
    'compliance_records.create': 'Compliance record created',
    'compliance_records.verify': 'Record verified',
    'compliance_records.supersede': 'Record superseded',
    'compliance_records.expired': 'Record expired',
    'compliance_records.evidence_attached': 'Evidence attached on behalf',
    'compliance_records.request_reverification': 'Re-verification requested',
    'still_active_response.submitted': '"Still active" response received',
    'magic_link_token.issued': 'Magic-link token issued',
    'magic_link_token.redeemed': 'Magic-link token redeemed',
    'volunteer_session.created': 'Volunteer signed in',
  };
  if (map[action]) return map[action]!;
  // Fallback: use the leaf segment, replace underscores, capitalise.
  const leaf = action.split('.').slice(-1)[0] ?? action;
  return leaf.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
